import { extendTheme, StyleFunctionProps } from '@chakra-ui/react';
import { focusStyles, theme as udsTheme } from '@udacity/chakra-uds-theme';
import { headerHeight } from '@udacity/udacity-header-v2';

import { barlow_condensed, inter, plus_jakarta_sans } from '../fonts/v2';

import { reactDatePickerGlobalStyles } from './react-datepicker-globals';

export const v2Theme = extendTheme(udsTheme, {
  styles: {
    global: {
      'html, body, #__next': {
        height: '100%',
        scrollBehavior: 'smooth',
        overscrollBehavior: 'none',
        // Offset scroll position for fixed header when navigating to # anchors or using scrollIntoView
        scrollPaddingTop: `calc(${headerHeight} + 1rem)`,
      },
      body: {
        bgColor: 'white',
        overflowX: 'hidden',
      },
      '*::selection': {
        background: 'supportBlue.40',
        color: 'black',
      },
      strong: {
        fontWeight: '600',
      },
      '#faqs .chakra-accordion': {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      },
      '#faqs .chakra-accordion__item:has(.chakra-accordion__button[aria-expanded="true"]) .chakra-accordion__panel': {
        fontSize: 'sm',
        color: 'gray.900',
      },
      '.uds-carousel .swiper-scrollbar': {
        top: 'var(--carousel-scrollbar-offset-top, 20px)',
        right: '136px',
      },
      '.uds-carousel--rtl .swiper-scrollbar': {
        right: 'unset',
        left: '136px',
      },
      '.uds-carousel .swiper-wrapper': {
        gap: '16px',
      },
      '.uds-carousel .swiper-slide': {
        margin: '0 !important',
      },
      '.youtube-container': {
        width: '100%',
        height: 'auto',
        aspectRatio: '16 / 9',
      },
      '.legal-text-container .ureact-markdown p': {
        fontSize: 'sm',
        lineHeight: 'sm',
        color: 'black',
      },
      '.legal-text-container .ureact-markdown a': {
        fontSize: 'sm',
        lineHeight: 'sm',
        color: 'primary.blue',
      },
      '.PhoneInput': {
        gap: '8px',
      },
      '.PhoneInputCountrySelect:focus + .PhoneInputCountryIcon + .PhoneInputCountrySelectArrow': {
        '--PhoneInputCountrySelectArrow-color--focus': '#175CFF',
      },
      '.PhoneInputCountry': {
        height: '2.5rem',
        alignSelf: 'flex-end',
        padding: '0 12px',
        border: 'solid 1px',
        borderColor: 'grey.500',
        rounded: 'base',
        gap: '12px',
        marginRight: 0,
      },
      '.PhoneInputCountrySelectArrow': {
        color: 'grey.500',
      },
      '.brow-banner': {
        '*': {
          _focus: focusStyles('white'),
        },
      },
      '[id^="react-select-select-instance"]': {
        fontSize: '0.8125rem',
        lineHeight: '1.4',
      },
      ...reactDatePickerGlobalStyles,
    },
  },
  components: {
    Alert: {
      defaultProps: {
        variant: 'subtle',
        size: 'md',
        status: 'info',
      },
      variants: {
        v2: {
          container: {
            bg: 'supportGray.10',
            border: 'solid 1px',
            borderColor: 'grey.100',
            boxShadow: 'none',
            borderStartRadius: '0',
            borderEndRadius: '8px',
            borderStartWidth: '0',
            p: '12px',
            position: 'relative',
            gap: '12px',
            '.chakra-alert__desc': {
              padding: 0,
              width: '100%',
            },
            '[data-status="success"]::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              'inset-inline-start': 0,
              width: '2px',
              height: '100%',
              backgroundColor: 'green.500',
            },
            '[data-status="info"]::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              'inset-inline-start': 0,
              width: '2px',
              height: '100%',
              backgroundColor: 'blue.500',
            },
            '[data-status="warning"]::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              'inset-inline-start': 0,
              width: '2px',
              height: '100%',
              backgroundColor: 'orange.500',
            },
            '[data-status="error"]::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              'inset-inline-start': 0,
              width: '2px',
              height: '100%',
              backgroundColor: 'red.300',
            },
          },
          icon: {
            borderRadius: 'full',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            alignSelf: 'center',
            me: 0,
            p: '0',
            '&[data-status="success"]': {
              bg: 'green.100 !important',
              color: 'green.500',
            },
            '&[data-status="info"]': {
              bg: 'blue.100 !important',
              color: 'blue.500',
            },
            '&[data-status="warning"]': {
              bg: 'supportOrange.20 !important',
              color: 'orange.500',
            },
            '&[data-status="error"]': {
              bg: 'red.100 !important',
              color: 'red.500',
            },
          },
        },
      },
    },
    Menu: {
      variants: {
        v2: {
          list: {
            borderRadius: '12px',
            borderColor: 'grey.100',
            minWidth: '120px',
          },
          item: {
            fontSize: '0.75rem',
            fontWeight: '500',
            color: 'grey.900',
            py: '2px !important',
            _hover: {
              bg: 'blue.50',
            },
          },
        },
      },
    },
    Input: {
      variants: {
        outline: {
          field: {
            height: '2.5rem',
            borderColor: 'grey.500',
            borderRadius: 'base',
            bg: 'white',
            padding: '0.75rem 1rem',
            fontSize: '1rem',
            _hover: {
              boxShadow: `0 0 0 2px primary.blue`,
              borderColor: 'primary.blue',
            },
            _focus: {
              boxShadow: `0 0 0 4px primary.blue`,
            },
            _invalid: {
              boxShadow: `0 0 0 2px primary.blue`,
            },
            _disabled: {
              opacity: 0.5,
              _hover: {
                boxShadow: 'none',
                borderColor: 'grey.500',
              },
            },
            _readOnly: {
              bg: 'supportGray.10',
              color: 'black',
              _hover: {
                borderColor: 'grey.500',
              },
              _focus: {
                borderColor: 'grey.500',
              },
            },
            _placeholder: {
              color: 'grey.900',
            },
          },
        },
        v2: {
          field: {
            borderRadius: '4px',
            border: 'solid 1px',
            borderColor: 'grey.200',
          },
        },
      },
    },
    FormLabel: {
      variants: {
        v2: {
          fontSize: 'sm',
          fontWeight: '500',
          color: 'grey.900',
          mb: '4px',
        },
      },
    },
    Checkbox: {
      baseStyle: {
        control: {
          borderWidth: '1px',
          borderRadius: '2px',
          borderColor: 'grey.300',
        },
        label: {
          display: 'inline-flex',
        },
      },
      variants: {
        v2: {
          control: {
            w: '16px',
            h: '16px',
          },
          label: {
            ml: '4px',
          },
        },
      },
    },
    Radio: {
      baseStyle: {
        control: {
          borderWidth: '1px',
          borderColor: 'grey.300',
        },
      },
    },
    // Credit: https://github.com/chakra-ui/chakra-ui/issues/2893#issuecomment-780143150
    // This is a workaround to create drawers that allow users to interact with the content behind it while the drawer is open.
    Drawer: {
      variants: {
        alwaysOpen: {
          parts: ['dialog, dialogContainer'],
          dialog: {
            pointerEvents: 'auto',
          },
          dialogContainer: {
            pointerEvents: 'none',
          },
        },
        mobile: {
          parts: ['header'],
          header: {
            shadow: 'md',
            zIndex: 1,
            height: '56px',
          },
          closeButton: { position: 'relative', top: 'unset', right: 'unset' },
          footer: {
            shadow: '0px -2px 4px -1px #0B0B0B0F,0px -4px 6px -1px #0B0B0B1A;',
          },
        },
        woolfMasters: {
          parts: ['dialog'],
          dialog: {
            pointerEvents: 'auto',
            maxWidth: '356px',
            bgColor: 'grey.100',
          },
        },
      },
    },
    Heading: {
      sizes: {
        'v2-h1': {
          fontFamily: 'heading',
          fontSize: '3rem',
          lineHeight: '52px',
          fontWeight: '600',
        },
        'v2-h2': {
          fontFamily: 'heading',
          fontSize: '2rem',
          lineHeight: '38px',
          fontWeight: '600',
        },
        'v2-h3': {
          fontFamily: 'heading',
          fontSize: '1.75rem',
          lineHeight: '34px',
          fontWeight: '600',
        },
        'v2-h4': {
          fontFamily: 'heading',
          fontSize: '1.5rem',
          lineHeight: '30px',
          fontWeight: '600',
        },
        'v2-h5': {
          fontFamily: 'heading',
          fontSize: '1.25rem',
          lineHeight: '26px',
          fontWeight: '600',
        },
        'v2-h6': {
          fontFamily: 'heading',
          fontSize: '1rem',
          lineHeight: '22px',
          fontWeight: '600',
        },
        'heading-8': {
          fontFamily: 'heading',
          fontSize: '1.125rem',
          lineHeight: '1.5rem',
          fontWeight: '600',
        },
        'heading-7': {
          fontFamily: 'heading',
          fontSize: '1.25rem',
          lineHeight: '1.625rem',
          fontWeight: '600',
        },
        'heading-6': {
          fontFamily: 'heading',
          fontSize: '1.625rem',
          lineHeight: '1.875rem',
          fontWeight: '600',
        },
        'heading-5': {
          fontFamily: 'heading',
          fontSize: '2rem',
          lineHeight: '2.5rem',
          fontWeight: '600',
        },
        'heading-4': {
          fontFamily: 'heading',
          fontSize: '2.375rem',
          lineHeight: '2.625rem',
          fontWeight: '600',
        },
        'heading-3': {
          fontFamily: 'heading',
          fontSize: '2.25rem',
          lineHeight: '2.25rem',
          fontWeight: '600',
        },
        'heading-2': {
          fontFamily: 'heading',
          fontSize: '3.375rem',
          lineHeight: '110%',
          fontWeight: '600',
        },
        'heading-1': {
          fontFamily: 'heading',
          fontSize: '3.5rem',
          lineHeight: '95%',
          fontWeight: '600',
        },
        'mobile-heading-5': {
          fontFamily: 'heading',
          fontSize: '1.125rem',
          lineHeight: '110%',
          fontWeight: '600',
        },
        'mobile-heading-3': {
          fontFamily: 'heading',
          fontSize: '1.75rem',
          lineHeight: '110%',
          fontWeight: '600',
        },
        'mobile-heading-2': {
          fontFamily: 'heading',
          fontSize: '2.5rem',
          lineHeight: '110%',
          fontWeight: '600',
        },
        // Note: Legacy marketing heading sizes
        // Please do not use these in the newly designed pages.
        'marketing-6xl': {
          fontFamily: 'heading',
          fontSize: '3.875rem',
          lineHeight: '4rem',
          fontWeight: '400',
        },
        'marketing-5xl': {
          fontFamily: 'heading',
          fontSize: '3rem',
          lineHeight: '3.25rem',
          fontWeight: '400',
        },
        'marketing-4xl': {
          fontFamily: 'heading',
          fontSize: '2.5rem',
          lineHeight: '3rem',
          fontWeight: '400',
        },
        'marketing-3xl': {
          fontFamily: 'heading',
          fontSize: '2rem',
          lineHeight: '2.5rem',
          fontWeight: '400',
        },
        'marketing-2xl': {
          fontFamily: 'heading',
          fontSize: '1.75rem',
          lineHeight: '2.1875rem',
          fontWeight: '400',
        },
        'marketing-xl': {
          fontFamily: 'heading',
          fontSize: '1.5rem',
          lineHeight: '1.875rem',
          fontWeight: '400',
        },
        'marketing-lg': {
          fontFamily: 'heading',
          fontSize: '1.25rem',
          lineHeight: '1.6875rem',
          fontWeight: '400',
        },
        'marketing-md': {
          fontFamily: 'heading',
          fontSize: '1rem',
          lineHeight: '1.5rem',
          fontWeight: '400',
        },
      },
    },
    Switch: {
      variants: {
        custom: {
          parts: ['track'],
          track: {
            bg: 'accent.sea-foam.600 !important',
          },
        },
      },
      sizes: {
        sm: (props: StyleFunctionProps) => {
          return {
            track: {
              width: '2.125rem',
              height: '1.25rem',
              padding: '0.125rem',
            },
            thumb: {
              width: '1rem',
              height: '1rem',
              _checked: {
                transform: `translateX(${props.dir === 'rtl' ? '-0.875rem' : '0.875rem'})`,
              },
            },
          };
        },
      },
    },
    Button: {
      baseStyle: {
        borderRadius: '4px',
        fontWeight: '400',
      },
      variants: {
        outline: (props: StyleFunctionProps) => {
          if (props.colorScheme === 'blue') {
            return {
              boxShadow: `inset 0 0 0 1.5px ${props.theme.colors.primary.blue}`,
              color: props.theme.colors.primary.blue,
              fill: props.theme.colors.primary.blue,
              _hover: {
                backgroundColor: 'secondary.blue',
              },
              _disabled: {
                boxShadow: `inset 0 0 0 1.5px ${props.theme.colors.supportGray['30']}`,
                color: props.theme.colors.supportGray['30'],
                fill: props.theme.colors.supportGray['30'],
              },
              _active: {
                bgColor: 'supportBlue.80',
                outlineColor: props.theme.colors.primary.blue,
              },
            };
          }

          return {};
        },
        solid: (props: StyleFunctionProps) => {
          if (props.colorScheme === 'blue') {
            return {
              bgColor: 'primary.blue',
              _hover: {
                bgColor: 'secondary.blue',
              },
            };
          }

          if (props.colorScheme === 'crimson') {
            return {
              bgColor: 'crimson.500',
              color: 'white',
              _hover: {
                bgColor: 'crimson.700',
              },
            };
          }

          if (props.colorScheme === 'white') {
            return {
              bgColor: 'white',
              color: 'supportGray.100',
              _hover: {
                bgColor: 'primary.blue',
                color: 'white',
              },
            };
          }

          if (props.colorScheme === 'blue-to-purple') {
            return {
              bgGradient: 'linear(to-r, primary.blue, primary.purple)',
              color: 'white',
              border: 'solid 1px',
              borderColor: 'grey.200',
              boxShadow: '0px 2px 4px 0px rgba(0,0,0,0.16)',
              _hover: {
                bgGradient: 'linear(to-r, secondary.blue, secondary.blue)',
                color: 'white',
                boxShadow: '0px 4px 8px 0px rgba(0,0,0,0.32)',
              },
            };
          }
        },
      },
    },
    Tag: {
      baseStyle: {
        container: {
          border: '1px solid',
          borderColor: '#AEB8CD33',
          boxShadow: '1px 1px 2px 0px #FFFFFFBF inset',
          padding: '.25rem .5rem',
          borderRadius: '8px',
          fontWeight: '600',
          fontSize: 'sm',
          lineHeight: 'sm',
        },
      },
      variants: {
        solid: (props: StyleFunctionProps) => {
          const { colorScheme } = props;

          if (colorScheme === 'blue') {
            return {
              container: {
                bgGradient: 'linear(180deg, #DDEDFA 0%, #C8E6FD 100%)',
                borderColor: '#3F7DFF33',
                color: 'supportBlue.80',
                lineHeight: '1',
              },
            };
          }

          if (colorScheme === 'orange') {
            return {
              container: {
                bgGradient: 'linear(180deg, #FDEFE0 0%, #EED5BE 100%)',
                color: 'supportOrange.70',
                borderColor: '#FB570933',
                lineHeight: '1',
              },
            };
          }

          if (colorScheme === 'purple') {
            return {
              container: {
                bgGradient: 'linear(180deg, #F4E9FF 0%, #DBC8FD 100%)',
                color: '#7134D2',
                borderColor: '#9759FA33',
                lineHeight: '1',
              },
            };
          }

          if (colorScheme === 'green') {
            return {
              container: {
                bgGradient: 'linear(180deg, #D5F6DE 0%, #BFEEBE 100%)',
                color: '#007540',
                borderColor: '#2BBB7C33',
                lineHeight: '1',
              },
            };
          }

          // Default color scheme (gray)
          return {
            container: {
              bgGradient: 'linear(180deg, #F3F6F9 0%, #E3EBF1 100%)',
              color: 'supportGray.70',
            },
          };
        },
      },
      sizes: {
        md: {
          container: {
            fontSize: 'xs',
            lineHeight: 'short',
            fontWeight: '600',
          },
        },
        lg: {
          container: {
            fontSize: 'md',
            lineHeight: 'base',
            fontWeight: '600',
            mb: 0,
          },
        },
      },
      defaultProps: {
        variant: 'solid',
        size: 'md',
      },
    },
    Link: {
      baseStyle: (props: StyleFunctionProps) => {
        // Note: sea-foam is a legacy color scheme. It should be removed in the future.
        if (props.colorScheme === 'sea-foam') {
          return {
            color: '#00c5a1',
            _hover: {
              color: '#99e8d9',
            },
          };
        }
        return {
          color: 'primary.blue',
          _visited: {
            color: 'primary.blue',
          },
        };
      },
    },
    Modal: {
      baseStyle: {
        closeButton: {
          _hover: {
            bgColor: 'primary.blue',
            color: 'white',
          },
        },
      },
      variants: {
        v2: {
          dialog: {
            p: { base: '24px', md: '40px 48px' },
            borderRadius: { base: '0', md: '12px' },
            h: { base: '100dvh', md: 'fit-content' },
            overflowY: 'auto',
            margin: { base: 0, md: 'auto' },
          },
          header: {
            p: '0',
          },
          body: {
            p: '0',
          },
          footer: {
            p: '0',
            m: 0,
            border: 'none',
          },
          closeButton: {
            top: '12px',
            right: '8px',
            color: 'grey.300',
          },
        },
      },
    },
    Accordion: {
      variants: {
        v2: {
          container: {
            borderRadius: '4px',
            border: 'solid 1px !important',
            borderColor: 'grey.100 !important',
            borderLeftColor: 'grey.100 !important',
            borderRightColor: 'grey.100 !important',
            borderTopColor: 'grey.100 !important',
            borderBottomColor: 'grey.100 !important',
            bg: 'white',
            '.chakra-accordion__button[aria-expanded="true"]': {
              bg: 'blue.50',
              borderBottom: '1px !important',
              borderBottomColor: 'blue.100 !important',
              color: 'blue.500',
            },
          },
          button: {
            bg: 'grey.50',
            p: '16px 20px',
            borderTop: 'none',
            borderLeft: 'none',
            borderRight: 'none',
            borderRadius: '4px !important',
            fontWeight: '600',
            fontSize: '1rem',
            borderBottom: 'none !important',
            '&:hover': {
              color: 'blue.500',
            },
          },
          icon: {
            fontSize: '1.5rem',
          },
          panel: {
            border: 'none',
            p: '16px 20px',
          },
        },
      },
    },
  },
  sizes: {
    ['content-max-width-legacy']: '1120px',
    ['content-max-width']: '1250px',
    ['content-max-width-md']: '1056px',
    ['content-max-width-lg']: '1096px',
    ['content-max-width-xl']: '1142px',
  },
  fontSizes: {
    xs: '0.625rem',
    sm: '0.8125rem',
    md: '0.9375rem',
    lg: '1.0625rem',
  },
  lineHeights: {
    xs: '1rem',
    sm: '1.25rem',
    md: '1.375rem',
    lg: '1.625rem',
  },
  fonts: {
    heading: plus_jakarta_sans.style.fontFamily,
    body: inter.style.fontFamily,
    condensed: barlow_condensed.style.fontFamily,
  },
  colors: {
    primary: {
      blue: '#175CFF',
      orange: '#FF9B18',
      purple: '#9759FA',
    },
    secondary: {
      purple: '#E5D5FF',
      blue: '#11215A',
    },
    supportGray: {
      10: '#F9FBFD',
      20: '#E8E8E8',
      30: '#D1D1D1',
      40: '#B0B0B0',
      50: '#888888',
      60: '#6D6D6D',
      70: '#5D5D5D',
      90: '#454545',
      100: '#0B0B0B',
    },
    supportBlue: {
      10: '#F4FAFE',
      20: '#D7E8FF',
      30: '#B9D9FF',
      40: '#88C2FF',
      70: '#175CFF',
      80: '#0A41EB',
      100: '#242631',
    },
    supportOrange: {
      10: '#FFF7E8',
      20: '#FFEBC6',
      40: '#FF9B18',
      70: '#B73806',
    },
    blue: {
      50: '#F4F8FF',
      100: '#D2DFFF',
      300: '#7BA2FF',
      500: '#175CFF',
      700: '#00298C',
      900: '#031643',
    },
    grey: {
      50: '#F9F9F9',
      100: '#EEEEEE',
      200: '#CECFD4',
      300: '#9C9FA8',
      500: '#5A5C62',
      700: '#25262A',
      900: '#0A0B0F',
    },
    orange: {
      50: '#FFF9F5',
      100: '#FFF1E7',
      200: '#FFDCC3',
      300: '#FFB27D',
      500: '#EE7622',
      700: '#C35000',
      900: '#763000',
    },
    cyan: {
      50: '#F5FDFF',
      100: '#E1F8FF',
      300: '#89E6FF',
      500: '#00B0E0',
      700: '#007697',
    },
    purple: {
      50: '#FDF8FF',
      100: '#F6DFFF',
      300: '#E5A5FF',
      500: '#931CF5',
      700: '#6100b0',
    },
    crimson: {
      50: '#FFF9F9',
      100: '#FFE4E7',
      300: '#F4737F',
      500: '#9C0D22',
      600: '#7A0A1B',
      700: '#540916',
      900: '#2B050C',
    },
    slate: {
      500: '#5B6B7A',
    },
    gold: {
      /** Master's / MBA light gold (Figma “Master's Gold 100”) */
      100: '#FFDCA4',
      300: '#EDA93B',
      500: '#915B14',
    },
    green: {
      300: '#11B13C',
      500: '#0D872E',
    },
  },
  radii: {
    md: '0.75rem',
  },
  shadows: {
    sm: '0 12px 16px -8px rgba(0, 0, 0, 0.12)',
    cool: '0 1px 2px 0px rgba(0, 0, 0, 0.05), 0 4px 12px 0px rgba(0, 0, 0, 0.04), 0 6px 16px 0 rgba(37, 99, 235, .10)',
  },
});
